import React, { cloneElement, isValidElement, useCallback, useEffect, useMemo } from 'react';
import { List, ListItem, Typography, Box, createStyles, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import { isUrlInChildren } from 'new-design/components/CmtHelpers/JssHelper';
import { ArrowDownIcon } from 'assets/icons';
import { useIntl } from 'util/IntlMessages';
import { Button } from 'new-design/common/ui';
import { DesktopMenuItem } from './DesktopMenuItem';

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      '@keyframes slideIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      '@keyframes moveUp': {
        from: {
          marginTop: 20,
        },
        to: {
          marginTop: 0,
        },
      },
      navCollapse: {
        position: 'relative',
        marginRight: 10,
        '&:last-child': {
          marginRight: 0,
        },
        '& $navCollapseBtnInner': {
          padding: 10,
        },
        '& $navCollapseBtnInner .Cmt-icon-root path': {
          fill: theme.palette.secondary.main,
        },

        '& > .active > .active': {
          // boxShadow: '0px 4px 20px rgba(2, 44, 230, 0.5)',
        },

        '& > .Cmt-navCollapseBtn': {
          [theme.breakpoints.down('lg')]: {
            height: 46,
          },
        },
        '&.active > .Cmt-navCollapseBtn': {
          color: theme.palette.primary.main,
        },

        '&.active > .Cmt-navCollapseBtn $subCollapse .Cmt-navCollapseBtn.active': {
          color: theme.palette.common.black,
          '& $navCollapseBtnInner .Cmt-icon-root': {
            color: theme.palette.primary.main,
          },
          '&:hover, &:focus': {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.common.gray,
            '& $navCollapseBtnInner > .Cmt-icon-root': {
              color: theme.palette.common.black,
            },
          },
        },
      },
      navCollapseItem: {
        position: 'absolute',
        display: 'none',
        top: 46,
        left: 0,
        zIndex: 2,
        opacity: 0,
        visibility: 'hidden',
        [theme.breakpoints.down('lg')]: {
          top: 36,
        },
        '& > .submenu': {
          minWidth: 200,
          paddingTop: 10,
          paddingBottom: 10,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.common.black,
          borderRadius: theme.shape.borderRadius,
          boxShadow: '0px 4px 30px rgba(0, 0, 0, 0.08)',
          marginTop: 24,
          [theme.breakpoints.down('lg')]: {
            marginTop: 32,
          },
        },

        '& $navCollapseItem': {
          top: 50,
          left: '100%',
          right: 'auto',
        },
      },
      navCollapseBtn: {
        position: 'relative',
        padding: '0px 4px',
        cursor: 'pointer',
        color: theme.palette.primary.main,

        [theme.breakpoints.down('lg')]: {
          padding: 0,
        },

        '&:hover, &:focus': {
          color: theme.palette.primary.main,
        },

        '&:hover > $navCollapseItem': {
          display: 'block',
          opacity: 1,
          visibility: 'visible',
          animationName: '$slideIn, $moveUp',
          animationTimingFunction: 'ease, ease',
          animationDuration: '0.4s, 0.4s',
        },

        '& > $navCollapseItem $navCollapseBtn:hover > $navCollapseItem': {
          display: 'block',
          top: 0,
          opacity: 1,
          visibility: 'visible',
          animationName: '$slideIn, $moveUp',
          animationTimingFunction: 'ease, ease',
          animationDuration: '0.4s, 0.4s',
        },
      },
      navCollapseBtnInner: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: 16,
        minWidth: 88,
        [theme.breakpoints.down('lg')]: {
          minWidth: 80,
        },
        '&.active': {
          color: theme.palette.primary.main,
          '& .Cmt-name': {
            color: theme.palette.primary.main,
          },
          '& .Cmt-icon-root': {
            '& path': {
              fill: theme.palette.primary.main,
            },
          },
          '& .Cmt-navIcon': {
            '& path': {
              stroke: theme.palette.primary.main,
            },
          },
        },
        '& .Cmt-navIcon path': {
          stroke: theme.palette.secondary.main,
        },
        '&:hover': {
          '& .Cmt-name': {
            color: theme.palette.primary.main,
          },
          '& .Cmt-icon-root path': {
            fill: theme.palette.primary.main,
          },
          '& .Cmt-navIcon path': {
            stroke: theme.palette.primary.main,
          },
        },
      },
      navText: {
        flex: 1,
        fontSize: 18,
        letterSpacing: 0.012,
        lineHeight: '27px',
        color: theme.palette.secondary.main,
        [theme.breakpoints.down('lg')]: {
          fontSize: 14,
          lineHeight: '19px',
        },
      },
      iconRoot: {
        marginRight: 8,
        fontSize: 20,
        [theme.breakpoints.down('lg')]: {
          maxWidth: 20,
          maxHeight: 20,
        },
      },
      subCollapse: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.primary.main,
        '& .Cmt-navCollapseBtn': {
          color: theme.palette.primary.main,
          '&:hover, &:focus': {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.common.gray,
            '& .Cmt-icon-root': {
              color: theme.palette.common.black,
            },
          },
        },
        '&.active .Cmt-nav-menu-link.active': {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.primary.main,
          '& .Cmt-icon-root': {
            color: theme.palette.primary.main,
          },
          '&:hover, &:focus': {
            '& .Cmt-icon-root': {
              color: theme.palette.primary.main,
            },
          },
        },
      },
      navArrow: {},
      betaText: {
        marginLeft: theme.spacing(0.5),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down('lg')]: {
          color: theme.palette.primary.main,
          fontSize: 10,
          lineHeight: '23px',
        },
      },
    }),
  {
    name: 'DesktopMenuGroup',
  }
);

export const DesktopMenuGroup = ({ name, icon, className, link, children = [], type, beta = false, menuKey }) => {
  const history = useHistory();
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const isExpandable = useMemo(() => type === 'collapse', [type]);
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    setOpen(isUrlInChildren({ children, link }, history.location.pathname));

    const removeHistoryListening = history.listen((location) => {
      setOpen(isUrlInChildren({ children, link }, location.pathname));
    });

    return () => {
      removeHistoryListening();
    };
  }, [children, link, history]);

  const renderIcon = () => {
    if (icon && isValidElement(icon)) {
      return cloneElement(icon, {
        className: clsx(classes.iconRoot, 'Cmt-icon-root'),
      });
    }

    return null;
  };

  const MenuItemChildren = !!isExpandable && (
    <List component="div" disablePadding className={classes.navCollapseItem}>
      <Box className="submenu">
        {children.map((item, index) => {
          switch (item.type) {
            case 'collapse':
              return <DesktopMenuGroup {...item} key={index} className={classes.subCollapse} />;
            case 'item':
              return <DesktopMenuItem {...item} key={index} />;
            default:
              return null;
          }
        })}
      </Box>
    </List>
  );

  const goToLink = useCallback(() => {
    if (type !== 'collapse') {
      history.push(link);
    }
  }, [type, link]);

  const MenuCollapse = (
    <ListItem
      component="div"
      disableGutters
      className={clsx(classes.navCollapseBtn, 'Cmt-navCollapseBtn', {
        active: open,
      })}
    >
      <Button
        component="span"
        className={clsx(classes.navCollapseBtnInner, {
          active: open,
        })}
        onClick={goToLink}
      >
        {renderIcon()}
        <Typography component="span" className={clsx(classes.navText, 'Cmt-name')}>
          {name}
        </Typography>
        {beta && (
          <Typography className={clsx(classes.betaText, 'Cmt-nav-text')}>
            {formatMessage({ id: 'beta' })}
          </Typography>
        )}
        {!!isExpandable && <ArrowDownIcon className={clsx(classes.navArrow, 'Cmt-navIcon')} />}
      </Button>
      {MenuItemChildren}
    </ListItem>
  );

  return (
    <Box
      data-menu-key={menuKey}
      className={clsx(classes.navCollapse, 'Cmt-navCollapse', {
        [className]: !!className,
        active: open,
      })}
    >
      {MenuCollapse}
    </Box>
  );
};
